import React, { useEffect, useState } from "react";
import { Modal, Button, Select, Input, Form, InputNumber, Switch } from "antd";
import "./style.css";

const GenericModalForm = ({
  icon,
  iconBg = "#f3f4f6",
  title,
  subtitle,
  open,
  onOk,
  onCancel,
  initialValues = {},
  fields = [],
  okText = "Save Changes",
  cancelText = "Cancel",
  showProfileUpload = false,
  avatar,
  avatarPlaceholder = "PP",
  uploadButtonText = "Upload Photo",
  onUploadClick,
  onFileChange,
  fileInputRef,
  onValuesChange,
}) => {
  const [form] = Form.useForm();
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Callers often build initialValues from data that arrives after the modal
  // opens (e.g. role options fetched on edit), so re-apply it whenever its
  // contents change, skipping fields the user has already edited.
  const initialValuesKey = JSON.stringify(initialValues);

  useEffect(() => {
    if (open) {
      const untouchedValues = Object.fromEntries(
        Object.entries(initialValues).filter(
          ([name]) => !form.isFieldTouched(name),
        ),
      );
      form.setFieldsValue(untouchedValues);
    } else {
      form.resetFields();
      setConfirmLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form, initialValuesKey]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setConfirmLoading(true);
      await onOk(values);
    } catch (err) {
      console.log("Validation Failed:", err);
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <Modal
      className="generic-modal"
      open={open}
      onCancel={onCancel}
      title={null}
      footer={null}
      centered
      closable={!confirmLoading}
      maskClosable={!confirmLoading}
    >
      <div className="top-gradient"></div>

      <div className="modal-body-wrapper">
        <div className="modal-header">
          {icon && (
            <div
              className="modal-icon-wrapper"
              style={{ backgroundColor: iconBg }}
            >
              <img src={icon} alt="icon" />
            </div>
          )}
          <span className="modal-title">{title}</span>
        </div>

        {subtitle && <div className="modal-subtitle">{subtitle}</div>}

        {showProfileUpload && (
          <div className="profile-card">
            <div className="profile-header">
              <div className="btn">
                <Button
                  type="default"
                  className="upload-btn"
                  onClick={onUploadClick}
                >
                  {uploadButtonText}
                </Button>
              </div>

              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={onFileChange}
              />

              <div className="profile-avatar">
                {avatar ? (
                  <img
                    src={avatar}
                    alt="avatar"
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  avatarPlaceholder
                )}
              </div>
            </div>

            <div className="profile-body"></div>
          </div>
        )}

        <Form
          form={form}
          layout="vertical"
          className="modal-form"
          onValuesChange={(changed, all) =>
            onValuesChange?.(changed, all, form)
          }
        >
          {fields.map((field, idx) =>
            field.type === "switch" ? (
              <div className="switch-field-wrapper" key={idx}>
                <div className="switch-info">
                  <span className="switch-main-label">{field.label}</span>
                  {field.description && (
                    <p className="switch-description">{field.description}</p>
                  )}
                </div>
                <Form.Item
                  name={field.name}
                  valuePropName="checked"
                  initialValue={field.defaultValue ?? false}
                  rules={field.rules || []}
                  noStyle
                >
                  <Switch />
                </Form.Item>
              </div>
            ) : (
              <Form.Item
                key={idx}
                name={field.name}
                label={field.label}
                rules={field.rules || []}
                dependencies={field.dependencies || []}
                hasFeedback={field.type === "password"}
              >
                {field.type === "select" ? (
                  <Select
                    placeholder={field.placeholder || "Select an option"}
                    options={field.options?.map((opt) =>
                      typeof opt === "object" && opt !== null
                        ? opt
                        : { label: opt, value: opt },
                    )}
                    prefix={field.prefixIcon}
                    loading={field.loading}
                    onFocus={field.onFocus}
                    onDropdownVisibleChange={(open) => {
                      if (open) field.onFocus?.();
                    }}
                  />
                ) : field.type === "password" ? (
                  <Input.Password
                    placeholder={field.placeholder || ""}
                    prefix={field.prefixIcon}
                  />
                ) : field.type === "inputNumber" ? (
                  <InputNumber
                    style={{ width: "100%" }}
                    placeholder={field.placeholder || ""}
                    min={field.min}
                    max={field.max}
                    step={field.step || 0.01}
                    disabled={field.disabled}
                    stringMode={false}
                    onKeyPress={(event) => {
                      if (!/[0-9.]/.test(event.key)) {
                        event.preventDefault();
                      }
                    }}
                  />
                ) : (
                  <Input
                    type={field.type || "text"}
                    placeholder={field.placeholder || ""}
                    prefix={field.prefixIcon}
                    disabled={field.disabled}
                  />
                )}
              </Form.Item>
            ),
          )}
        </Form>

        <div className="modal-footer-wrapper">
          <Button onClick={onCancel} disabled={confirmLoading}>
            {cancelText}
          </Button>
          <Button type="primary" onClick={handleOk} loading={confirmLoading}>
            {okText}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default GenericModalForm;
